import React, { useState, useEffect } from 'react';
import { Settings, Play, BookOpen, Lightbulb, Trophy, Users, Book, History, ChevronRight, Sparkles } from 'lucide-react';
import MenuChallenges from './menu/MenuChallenges';

import MenuGameModes from './menu/MenuGameModes';
import MenuStreak from './menu/MenuStreak';
import MenuTrivia from './menu/MenuTrivia';
import MenuFooter from './menu/MenuFooter';
import GuidancePanel from './GuidancePanel';

import { useProgressionChecker } from '../hooks/useProgressionChecker';
import getSuggestions from '../utils/suggestions';
import { supabase } from '../lib/supabase';
import ConfirmModal from './ConfirmModal';
import { TIMELINE_ERAS } from '../data/timelineData';
import { useTimeline } from '../hooks/useTimeline';
import { LITERATURE } from '../data/literature';
import { SEO } from './SEO';
import AppHeader from './common/AppHeader';
import radioTowerCover from '../assets/images/adventure/radio-tower-cover.png';
import zork1Cover from '../assets/images/adventure/zork-1-cover.png';
import signalLampCover from '../assets/images/games/signal-lamp-cover.png';
import precisionLabCover from '../assets/images/games/precision-lab-cover.jpg';
import midnightExpressCover from '../assets/images/games/midnight-express.png';


const MenuView = ({
    userSettings,
    setView,
    wpm,
    setWpm,
    farnsworthWpm,
    setFarnsworthWpm,
    noiseLevel,
    setNoiseLevel,
    qsbLevel,
    setQsbLevel,
    inputMode,
    setInputMode,
    gameMode,
    setGameMode,
    callSignRegion,
    setCallSignRegion,
    activityType,
    setActivityType,
    activityRole,
    setActivityRole,
    contestType,
    setContestType,
    qsoRole,
    setQsoRole,
    startGame,
    progress,
    setLevel,
    dailyChallenge,
    dailyDefinition,
    weeklyChallenge,
    weeklyDefinition,
    pileupCount,
    setPileupCount,
    unreadSocialCount,
    customChars,
    setCustomChars,
    comprehensionMode,
    setComprehensionMode,
    setCurrentLiteratureId,
    setCurrentSegmentIndex,
    dailyTrivia,
    setKnowledgeTab,
    user,
    audio,
    quizInputMode,
    setQuizInputMode
}) => {
    // Progression tracking
    const { progressionStatus, checkProgression } = useProgressionChecker(userSettings, progress);
    const [dismissedBanner, setDismissedBanner] = useState(false);
    const [showAdvanceModal, setShowAdvanceModal] = useState(false);
    const [advancedLevel, setAdvancedLevel] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);

    // Pick a random cover for the Literature Library button
    const libraryCover = React.useMemo(() => {
        const coveredItems = LITERATURE.filter(item => item.coverImage);
        if (coveredItems.length === 0) return null;
        return coveredItems[Math.floor(Math.random() * coveredItems.length)].coverImage;
    }, []);

    // Pick a random cover for the Morse Game Center button
    const gameCenterCover = React.useMemo(() => {
        const covers = [
            radioTowerCover,
            zork1Cover,
            signalLampCover,
            midnightExpressCover,
            '/assets/constellations/ursa_major.png'
        ];
        return covers[Math.floor(Math.random() * covers.length)];
    }, []);

    const labCover = React.useMemo(() => {
        return precisionLabCover;
    }, []);

    // Timeline progress for dynamic icon
    const { events: userEvents, loading: timelineLoading } = useTimeline(user);

    // Calculate the active era (earliest with locked/incomplete events)
    const activeTimelineEra = React.useMemo(() => {
        if (timelineLoading || !userEvents) return TIMELINE_ERAS[0];

        const firstEraWithUnfinishedEvents = TIMELINE_ERAS.find(era => {
            if (!era.events || era.events.length === 0) return false;
            return era.events.some(event => !userEvents[event.id]?.completed);
        });

        return firstEraWithUnfinishedEvents || TIMELINE_ERAS[TIMELINE_ERAS.length - 1];
    }, [userEvents, timelineLoading]);

    // Fetch recent activities for suggestions
    useEffect(() => {
        const fetchActivities = async () => {
            const { data: user } = await supabase.auth.getUser();
            if (user?.user) {
                const { data } = await supabase
                    .from('activities')
                    .select('*')
                    .eq('user_id', user.user.id)
                    .order('created_at', { ascending: false })
                    .limit(10);

                if (data) {
                    setRecentActivities(data);
                }
            }
        };

        fetchActivities();
    }, []);

    // Generate suggestions when skill level or activities change
    useEffect(() => {
        const newSuggestions = getSuggestions(
            userSettings?.skill_level || 1,
            recentActivities,
            userSettings
        );
        setSuggestions(newSuggestions);
    }, [userSettings?.skill_level, recentActivities, userSettings]);

    const handleSuggestionClick = (suggestion) => {
        if (suggestion.view) {
            setView(suggestion.view);
            return;
        }

        if (suggestion.gameMode) {
            setGameMode(suggestion.gameMode);
            // Scroll to the start button specifically
            setTimeout(() => {
                const startBtn = document.getElementById('start-training-button');
                if (startBtn) {
                    startBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    // Fallback to h3 if start button selector fails
                    document.querySelector('h3')?.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        }
    };

    const handleAdvance = async () => {
        const newLevel = (userSettings.skill_level || 1) + 1;

        // Update in database
        const { data: user } = await supabase.auth.getUser();
        if (user?.user) {
            const { error } = await supabase
                .from('profiles')
                .update({
                    skill_level: newLevel,
                    skill_level_updated_at: new Date().toISOString()
                })
                .eq('id', user.user.id);

            if (!error) {
                // Record progression in history
                await supabase.from('skill_progression').insert({
                    user_id: user.user.id,
                    from_level: newLevel - 1,
                    to_level: newLevel,
                    criteria_met: progressionStatus,
                    auto_promoted: true
                });
            }
        }

        // Show success modal instead of alert
        setAdvancedLevel(newLevel);
        setShowAdvanceModal(true);
        setDismissedBanner(true);
    };

    const handleStartTraining = () => {
        if (gameMode === 'icr_comprehension' && (comprehensionMode === 'literature' || comprehensionMode === 'stories' || comprehensionMode === 'poems')) {
            setView('literature_library');
        } else {
            startGame();
        }
    };

    return (
        <div
            className="min-h-screen bg-base text-main flex items-center justify-center p-4 transition-colors duration-300"
            style={{ fontVariantNumeric: 'slashed-zero' }}
        >
            <SEO
                title="Training Menu"
                description="Choose your Morse Code training mode. Practice Koch method, copy callsigns, simulate QSOs, or explore the history of telegraphy."
                type="website"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Keyer's Journey",
                    "url": "https://keyersjourney.com",
                    "description": "Professional Morse Code training application featuring Koch method, analyzing tools, and historical context."
                }}
            />
            <div className="max-w-md w-full space-y-8">
                <AppHeader
                    title="Keyer's Journey"
                    subtitle="Training, History & Adventure"
                    titleBelow={true}
                    leftSlot={
                        <button
                            className="flex items-center gap-2 text-muted hover:text-primary transition-colors group"
                            onClick={() => setView('settings/profile')}
                            title="Settings & Profile"
                        >
                            <div className="p-2 bg-surface rounded-full border border-border group-hover:border-primary/50 transition-colors shadow-sm">
                                <Settings size={20} />
                            </div>
                            <span className="font-bold text-sm opacity-75 group-hover:opacity-100 max-w-[100px] truncate">
                                {userSettings.callSign || 'Setup'}
                            </span>
                        </button>
                    }
                    rightSlot={
                        <div
                            className="transition-transform active:scale-95"
                            onClick={() => setView('stats')}
                            title="View Stats"
                        >
                            <MenuStreak
                                streak={userSettings.streak}
                                lastPlayedDate={userSettings.lastPlayedDate}
                                freezeCount={userSettings.streakFreezes ?? 1}
                            />
                        </div>
                    }
                />

                <MenuChallenges
                    dailyChallenge={dailyChallenge}
                    dailyDefinition={dailyDefinition}
                    weeklyChallenge={weeklyChallenge}
                    weeklyDefinition={weeklyDefinition}
                />

                {/* Daily Trivia */}
                <MenuTrivia
                    trivia={dailyTrivia}
                    audio={audio}
                    wpm={wpm}
                    farnsworth={farnsworthWpm}
                    onClick={() => {
                        setKnowledgeTab('trivia');
                        setView('knowledge');
                    }}
                />

                {/* Timeline Feature Card */}
                <button
                    onClick={() => {
                        setKnowledgeTab('timeline');
                        setView('knowledge');
                    }}
                    className="w-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 hover:border-primary/60 hover:from-primary/15 hover:to-primary/10 rounded-2xl p-6 relative group transition-all duration-300 transform hover:scale-[1.01] shadow-lg mb-6"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden border border-primary/20 bg-primary/10 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                <img
                                    src={activeTimelineEra.iconImage}
                                    alt={activeTimelineEra.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="text-left">
                                <h2 className="text-xl font-bold text-main">Morse History Timeline</h2>
                                <p className="text-sm text-muted">Next: {activeTimelineEra.title}</p>
                            </div>
                        </div>
                        <div className="text-primary opacity-50 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 duration-300">
                            <ChevronRight size={24} />
                        </div>
                    </div>
                </button>

                {/* Literature Library Card */}
                <button
                    onClick={() => setView('literature_library')}
                    className="w-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 hover:border-primary/60 hover:from-primary/15 hover:to-primary/10 rounded-2xl p-6 relative group transition-all duration-300 transform hover:scale-[1.01] shadow-lg mb-6"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden border border-primary/20 bg-primary/10 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-inner flex items-center justify-center text-primary">
                                {libraryCover ? (
                                    <img src={libraryCover} alt="Library" className="w-full h-full object-cover object-top" />
                                ) : (
                                    <BookOpen size={32} />
                                )}
                            </div>
                            <div className="text-left relative">
                                <h2 className="text-xl font-bold text-main">Literature Library</h2>
                                <p className="text-sm text-muted">Poetry & Stories for Head Copy</p>
                                <span className="absolute -top-1 -right-8 bg-primary text-primary-content text-[10px] font-black px-1.5 py-0.5 rounded-md shadow-lg transform rotate-12 scale-90 animate-pulse">
                                    NEW
                                </span>
                            </div>
                        </div>
                        <div className="text-primary opacity-50 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 duration-300">
                            <ChevronRight size={24} />
                        </div>
                    </div>
                </button>

                {/* Morse Game Center Card */}
                <button
                    onClick={() => setView('gamecenter')}
                    className="w-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 hover:border-primary/60 hover:from-primary/15 hover:to-primary/10 rounded-2xl p-6 relative group transition-all duration-300 transform hover:scale-[1.01] shadow-lg mb-6"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden border border-primary/20 bg-primary/10 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-inner flex items-center justify-center text-primary">
                                <img
                                    src={gameCenterCover}
                                    alt="Games"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="text-left relative">
                                <h2 className="text-xl font-bold text-main">Morse Game Center</h2>
                                <p className="text-sm text-muted">Arcade games & skill challenges</p>
                                <span className="absolute -top-1 -right-8 bg-primary text-primary-content text-[10px] font-black px-1.5 py-0.5 rounded-md shadow-lg transform rotate-12 scale-90 animate-pulse">
                                    NEW
                                </span>
                            </div>
                        </div>
                        <div className="text-primary opacity-50 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 duration-300">
                            <ChevronRight size={24} />
                        </div>
                    </div>
                </button>

                {/* Precision Lab Card - Temporarily Removed
                <button
                    onClick={() => setView('lab')}
                    className="w-full bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30 hover:border-emerald-500/60 hover:from-emerald-500/15 hover:to-emerald-500/10 rounded-2xl p-6 relative group transition-all duration-300 transform hover:scale-[1.01] shadow-lg mb-6"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl overflow-hidden border border-emerald-500/20 bg-emerald-500/10 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-inner flex items-center justify-center text-primary">
                                <img
                                    src={labCover}
                                    alt="Precision Lab"
                                    className="w-full h-full object-cover object-top"
                                />
                            </div>
                            <div className="text-left relative">
                                <h2 className="text-xl font-bold text-main">Precision Lab</h2>
                                <p className="text-sm text-muted">Diagnostic & mechanical drills</p>
                                <span className="absolute -top-1 -right-8 bg-emerald-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-md shadow-lg transform rotate-12 scale-90 animate-pulse">
                                    NEW
                                </span>
                            </div>
                        </div>
                        <div className="text-emerald-400 opacity-50 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 duration-300">
                            <ChevronRight size={24} />
                        </div>
                    </div>
                </button>
                */}


                <div className="bg-surface p-6 rounded-2xl shadow-2xl border border-border space-y-6 transition-colors duration-300">
                    <div className="pb-4 border-b border-border">
                        <h3 className="font-bold text-lg text-main">Training</h3>
                    </div>

                    <div className="space-y-3">
                        <h4 className="text-[10px] font-bold text-muted uppercase tracking-wider">Configuration</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {/* Audio Settings Link */}
                            <button
                                onClick={() => setView('settings/audio')}
                                className="bg-base/50 p-3 rounded-xl border border-border hover:bg-surface-hover hover:border-primary/50 transition-all text-left flex flex-col gap-1 group"
                            >
                                <div className="flex items-baseline justify-between">
                                    <span className="text-[10px] text-muted uppercase font-bold tracking-wider group-hover:text-primary transition-colors">Audio</span>
                                    <span className="font-bold text-muted group-hover:text-main text-sm">{wpm} WPM</span>
                                </div>
                                <div className="text-[10px] text-muted/70 flex items-center gap-1">
                                    <span className={farnsworthWpm < wpm ? 'text-warning' : ''}>{farnsworthWpm} FW</span>
                                    <span>•</span>
                                    <span>QRN {noiseLevel}</span>
                                </div>
                            </button>

                            {/* Input Mode Link */}
                            <button
                                onClick={() => setView('settings/input')}
                                className="bg-base/50 p-3 rounded-xl border border-border hover:bg-surface-hover hover:border-primary/50 transition-all text-left flex flex-col gap-1 group"
                            >
                                <div className="flex items-baseline justify-between">
                                    <span className="text-[10px] text-muted uppercase font-bold tracking-wider group-hover:text-primary transition-colors">Input</span>
                                    <span className="font-bold text-muted group-hover:text-main text-sm truncate capitalize">
                                        {inputMode === 'straight' ? 'Straight' : inputMode}
                                    </span>
                                </div>
                                <div className="text-[10px] text-muted/70">
                                    {inputMode === 'keyboard' ? 'Receive only' : 'Send & receive'}
                                </div>
                            </button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-xs font-bold text-muted uppercase tracking-wider">Guidance</h4>
                        <GuidancePanel
                            progressionStatus={progressionStatus}
                            suggestions={suggestions}
                            onAdvance={handleAdvance}
                            onSelectSuggestion={handleSuggestionClick}
                            userSettings={userSettings}
                            onDismiss={() => setDismissedBanner(true)}
                            embedded={true}
                        />
                    </div>

                    <div className="space-y-4">
                        <MenuGameModes
                            gameMode={gameMode}
                            setGameMode={setGameMode}
                            progress={progress}
                            setLevel={setLevel}
                            callSignRegion={callSignRegion}
                            setCallSignRegion={setCallSignRegion}
                            activityType={activityType}
                            setActivityType={setActivityType}
                            activityRole={activityRole}
                            setActivityRole={setActivityRole}
                            contestType={contestType}
                            setContestType={setContestType}
                            qsoRole={qsoRole}
                            setQsoRole={setQsoRole}
                            userSettings={userSettings}
                            setView={setView}
                            pileupCount={pileupCount}
                            setPileupCount={setPileupCount}
                            customChars={customChars}
                            setCustomChars={setCustomChars}
                            comprehensionMode={comprehensionMode}
                            setComprehensionMode={setComprehensionMode}
                            inputMode={inputMode}
                            startGame={startGame}
                            setCurrentLiteratureId={setCurrentLiteratureId}
                            setCurrentSegmentIndex={setCurrentSegmentIndex}
                            quizInputMode={quizInputMode}
                            setQuizInputMode={setQuizInputMode}
                        />
                    </div>

                    <button id="start-training-button" onClick={handleStartTraining} className="w-full flex items-center justify-center gap-3 bg-primary hover:bg-primary-hover text-primary-content font-bold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0">
                        <Play fill="currentColor" /> Start Training
                    </button>
                </div>

                {/* Navigation & Features */}
                <div className="bg-surface p-6 rounded-2xl shadow-2xl border border-border space-y-3 transition-colors duration-300">

                    <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => setView('learn')} className="flex items-center justify-center gap-2 text-muted hover:text-highlight py-3 text-sm bg-base/50 rounded-xl border border-border hover:bg-surface-hover transition-all">
                            <BookOpen size={16} /> Dictionary
                        </button>
                        <button onClick={() => {
                            setKnowledgeTab('guide');
                            setView('knowledge');
                        }} className="flex items-center justify-center gap-2 text-muted hover:text-highlight py-3 text-sm bg-base/50 rounded-xl border border-border hover:bg-surface-hover transition-all">
                            <Book size={16} /> User Guide
                        </button>
                        <button onClick={() => setView('stats')} className="flex items-center justify-center gap-2 text-muted hover:text-highlight py-3 text-sm bg-base/50 rounded-xl border border-border hover:bg-surface-hover transition-all">
                            <Trophy size={16} /> Stats
                        </button>
                        <button onClick={() => setView('leaderboard')} className="flex items-center justify-center gap-2 text-muted hover:text-highlight py-3 text-sm bg-base/50 rounded-xl border border-border hover:bg-surface-hover transition-colors">
                            <Trophy size={16} /> Leaders
                        </button>
                        <button onClick={() => setView('social')} className="col-span-2 relative flex items-center justify-center gap-2 text-primary hover:text-primary-hover py-3 text-sm bg-primary/10 rounded-xl border border-primary/20 hover:bg-primary/20 transition-colors">
                            <Users size={16} /> Social Hub
                            {unreadSocialCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-error text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                                    {unreadSocialCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                <MenuFooter />
            </div>

            {/* Level Advancement Success Modal */}
            <ConfirmModal
                isOpen={showAdvanceModal}
                onClose={() => setShowAdvanceModal(false)}
                onConfirm={() => window.location.reload()}
                title="🎉 Level Up!"
                message={`Congratulations! You've advanced to Level ${advancedLevel}! The page will reload to reflect your new level.`}
                confirmText="Reload Now"
                cancelText={null}
                type="success"
                showConfetti={true}
            />
        </div >
    );
};

export default MenuView;
